import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useI18n } from '@/ui/i18n/I18nProvider';
import styles from './UiErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  /** Called when the player closes the error card (close the panel that crashed, then try again). */
  onReset?: () => void;
}

/**
 * Keeps a crash in one UI layer from blanking the whole game: the room keeps running and the player gets a
 * small card to close what broke. The save is untouched (UI never writes state directly).
 */
export class UiErrorBoundary extends Component<Props, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('[ui]', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <UiErrorCard
        onClose={() => {
          this.props.onReset?.();
          this.setState({ failed: false });
        }}
      />
    );
  }
}

function UiErrorCard({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  return (
    <div className={styles.card} role="alert" data-testid="ui-error">
      <p className={styles.title}>{t('system.ui_error.title')}</p>
      <p className={styles.body}>{t('system.ui_error.body')}</p>
      <button type="button" className={styles.button} onClick={onClose}>
        {t('ui.common.close')}
      </button>
    </div>
  );
}
